/**
 * Transfer of Shares (M32201) 문서-뷰 변수 정의 + 영문 라벨/타입 규칙.
 *
 * OTC(otcFields) 미러링의 축소판 — 3문서 패키지(DCR / Section 105 / Waiver).
 *   · 반복부 transfers 는 flat tr{n}_* 키로 저장(패널이 transfers[] 배열과 브리지).
 *   · Section 105 는 단일 instrument(첫 transfer 기준) — 자동필드 + 증인/체크박스.
 * 변수명은 실제 docx 템플릿의 [[변수]] + transferSharesMapper 계약과 1:1.
 */

export type TransferFieldType = 'text' | 'date' | 'person' | 'address';

/** 반복부(transfer) 최대 행 수. */
export const TRANSFER_MAX = 20;

/** 작성 흐름 단계 정의 (스텝퍼). 순서 = 배열 순서, kind 로 렌더 방식 결정.
 *  DCR 을 먼저 작성(양도 반복부 정의) → 반복부에 따라 Section 105(양도별)·Share Certificate(양수인별) 가 ×N 확장. */
export interface TransferStage { no: number; kind: 'dcr' | 'waiver' | 'section105' | 'sharecert'; en: string }
export const TRANSFER_STAGES: TransferStage[] = [
  { no: 1, kind: 'dcr', en: "Board Resolution (Directors' Circular Resolution)" },
  { no: 2, kind: 'waiver', en: "Members' Consent (Waiver of Pre-emption)" },
  { no: 3, kind: 'section105', en: 'Instrument of Transfer (Section 105)' },
  { no: 4, kind: 'sharecert', en: 'Share Certificate' },
];

// ─── 라벨 ────────────────────────────────────────────────────────
const OVERRIDES: Record<string, string> = {
  company_name: 'Company Name', company_registration_no: 'Company Registration No.', old_registration_no: 'Old Registration No.',
  constitution_clause_number: 'Clause / Article No.',
  resolution_date: 'Resolution Date', transfer_date: 'Transfer Date', letter_date: 'Letter Date',
  total_shares_transferred: 'Total Shares Transferred',
  // Transfer row
  transferor_name: 'Transferor Name', transferee_name: 'Transferee Name',
  certificate_no: 'Certificate No.', new_certificate_no: 'New Certificate No.',
  shares: 'No. of Shares', new_shares: 'New No. of Shares', share_type: 'Class of Shares',
  // Section 105
  number_of_units: 'Number of Units', securities_description: 'Description of Securities',
  transferor_details: 'Transferor Name (Block Letters)', transferor_id: 'Transferor ID / Company No.',
  transferee_details: 'Transferee Name & Address', transferee_person: 'Transferee — ID / Nationality',
  transferee_corporate: 'Transferee — Company No. / Place of Incorporation',
  consideration_words: 'Consideration (in words)', consideration_figures: 'Consideration (RM)',
  witness_1_name: 'Witness 1 Name', witness_1_address: 'Witness 1 Address', witness_1_occupation: 'Witness 1 Occupation',
  witness_2_name: 'Witness 2 Name', witness_2_address: 'Witness 2 Address', witness_2_occupation: 'Witness 2 Occupation',
  witness_3_name: 'Witness 3 Name', witness_3_address: 'Witness 3 Address', witness_3_occupation: 'Witness 3 Occupation',
  witness_4_name: 'Witness 4 Name', witness_4_address: 'Witness 4 Address', witness_4_occupation: 'Witness 4 Occupation',
  // Share Certificate (Stage 4)
  cert_no: 'Certificate No.', no_of_shares: 'No. of Shares', member_name: 'Registered Holder',
  shares_numeral: 'No. of Shares (figures)', shares_words: 'No. of Shares (words)', share_class: 'Class of Shares',
  registered_office_address: 'Registered Office Address', issue_date: 'Issue Date',
};

const TOKENS: Record<string, string> = { no: 'No.', id: 'ID' };

/** tr{n}_transferor_name / witness_2_name 등 — 반복 인덱스를 정규화한 base key. */
function baseKey(key: string): string {
  return key.replace(/^tr\d+_/, '').replace(/_\d+$/, (m) => (/^witness/.test(key) ? m : ''));
}

/** tr{n}_ / s105_{n}_ / sc_{n}_ 반복 프리픽스 제거. (증인 키는 s105 내부에서 witness_k_ 로 유지) */
function stripRepeatPrefix(key: string): string {
  return key.replace(/^tr\d+_/, '').replace(/^s105_\d+_/, '').replace(/^sc_\d+_/, '');
}

export function labelize(key: string): string {
  const bare = stripRepeatPrefix(key);
  if (OVERRIDES[bare]) return OVERRIDES[bare];
  const b = baseKey(bare);
  if (OVERRIDES[b]) return OVERRIDES[b];
  return bare.split('_').map((w) => TOKENS[w] || (w.charAt(0).toUpperCase() + w.slice(1))).join(' ');
}

export function typeOf(key: string): TransferFieldType {
  const b = stripRepeatPrefix(key);
  if (/(^|_)(date)$/.test(b)) return 'date';
  if (/_address$/.test(b)) return 'address';
  // 이름/성명·주소 블록 = person lookup(✨). Section 105 항목 3(transferor_details)·5(transferee_details) 포함.
  if (/(transferor_name|transferee_name|member_name|transferor_details|transferee_details)$/.test(b)) return 'person';
  if (/^witness_\d+_name$/.test(b)) return 'person';
  return 'text';
}

/** 파생(읽기전용) 필드 — 문서에서 직접 편집 불가(상단/합계/자동매핑에서 결정). */
export const DERIVED_KEYS = new Set<string>([
  'total_shares_transferred', 'number_of_units', 'securities_description',
  // Share Certificate — 양도 표(New Cert / New Shares / Transferee)에서 자동 매핑.
  'cert_no', 'no_of_shares', 'shares_numeral', 'shares_words', 'member_name',
]);

/** 파생 여부 — 정확 키(DERIVED_KEYS) 또는 인덱스형 s105_{n}_/sc_{n}_ 자동매핑 필드. */
export function isDerivedKey(key: string): boolean {
  if (DERIVED_KEYS.has(key)) return true;
  if (/^s105_\d+_(number_of_units|securities_description)$/.test(key)) return true;
  return /^sc_\d+_(cert_no|no_of_shares|shares_numeral|shares_words|member_name)$/.test(key);
}

/** 반복부 transfer 행의 컬럼 키 (n 은 1-based). */
export function transferRowKeys(n: number) {
  return {
    transferorName: `tr${n}_transferor_name`,
    certificateNo: `tr${n}_certificate_no`,
    shares: `tr${n}_shares`,
    transfereeName: `tr${n}_transferee_name`,
    newCertificateNo: `tr${n}_new_certificate_no`,
    newShares: `tr${n}_new_shares`,
  };
}

/** key → transfer 행 index(0-based) + 컬럼. 반복부 필드가 아니면 null. */
export function parseTransferKey(key: string): { index: number; col: string } | null {
  const m = key.match(/^tr(\d+)_(.+)$/);
  if (!m) return null;
  return { index: Number(m[1]) - 1, col: m[2] };
}
